import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { api } from '../../lib/api';
import TemplateEditor from './TemplateEditor';

// SendOneOffEmailDialog — Sprint F item 6.7 / §3.10 action 3.
//
// Sends a single email to a chosen contact on an account, no campaign.
//
// Server contract (POST /api/admin/outreach/send-one-off):
//   200 { ok: true, sendId, stub?: true }   — stub: true means nothing left
//                                             this environment (dev/staging)
//   409 { code: 'opted_out' | 'suppressed' } — school opted out / address suppressed
//   502 { code: 'send_failed' }              — provider rejected the send
// The `api` client throws ApiError (.message/.code) on non-2xx, so a
// fulfilled promise here really does mean the send was accepted.

const PURPOSES = [
  { value: 'service',    label: 'Service message', hint: 'no marketing footer' },
  { value: 'commercial', label: 'Commercial',      hint: 'adds address + unsubscribe footer' },
];

export default function SendOneOffEmailDialog({ open, accountId, onClose, onSent }) {
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState('');
  const [subject, setSubject] = useState('Quick note from NexSav');
  const [bodyHtml, setBodyHtml] = useState('<p>Hi {{salutation}},</p><p>I wanted to reach out about {{school_name}}.</p>');
  const [bodyText, setBodyText] = useState('');
  const [customTokens, setCustomTokens] = useState([]);
  const [users, setUsers] = useState([]);
  const [fromUserId, setFromUserId] = useState('');
  const [purpose, setPurpose] = useState('service');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!open) return;
    setError(''); setNotice(''); setPurpose('service');
  }, [open]);

  useEffect(() => {
    if (!open || !accountId) return;
    let alive = true;
    Promise.all([
      api.get(`/api/admin/accounts/${accountId}/contacts`),
      api.get('/api/admin/users?role=nexsav_admin,ops'),
    ]).then(([cRes, uRes]) => {
      if (!alive) return;
      setContacts(cRes.contacts || []);
      const primary = (cRes.contacts || []).find((c) => c.isPrimary) || (cRes.contacts || [])[0];
      if (primary) setContactId(primary.id);
      const candidates = (uRes.users || []).filter((u) => /@nexsav\.net$/i.test(u.email || ''));
      setUsers(candidates);
      if (candidates.length > 0) setFromUserId(candidates[0].id);
    }).catch(() => {});
    return () => { alive = false; };
  }, [open, accountId]);

  const submit = async () => {
    setError(''); setNotice('');
    if (!contactId) { setError('Pick a recipient contact'); return; }
    setSubmitting(true);
    try {
      const r = await api.post('/api/admin/outreach/send-one-off', {
        account_id: accountId,
        contact_id: contactId,
        from_user_id: fromUserId || null,
        subject_template: subject,
        body_template_html: bodyHtml,
        body_template_text: bodyText,
        custom_tokens: Object.fromEntries(customTokens.map((c) => [c.key, c.value])),
        purpose,
      });
      if (r?.stub) {
        // Let the operator read the notice before the dialog goes away.
        setNotice('Recorded, but no email left this environment (stub mode).');
        setSubmitting(false);
        setTimeout(() => { onSent?.(r); onClose?.(); }, STUB_NOTICE_MS);
        return;
      }
      onSent?.(r);
      onClose?.();
    } catch (err) {
      // Keep the dialog open — the operator needs to see why nothing went out.
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title="Send one-off email"
      maxWidth="max-w-5xl"
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <button onClick={onClose} disabled={submitting} className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white">Cancel</button>
          <button
            data-testid="send-one-off-confirm"
            onClick={submit}
            disabled={submitting || !contactId}
            className="px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50 shadow-lg shadow-[#B0FC38]/20"
          >
            {submitting ? 'Sending…' : 'Send email'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Recipient *">
            <select data-testid="send-one-off-contact" value={contactId} onChange={(e) => setContactId(e.target.value)} className={selectCls}>
              <option value="">Select a contact…</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email || 'no email'})</option>)}
            </select>
          </Field>
          <Field label="Send as">
            <select data-testid="send-one-off-from" value={fromUserId} onChange={(e) => setFromUserId(e.target.value)} className={selectCls}>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} &lt;{u.email}&gt;</option>)}
            </select>
          </Field>
        </div>

        {/* Not wrapped in <Field> (a <label>) — nesting the radios' own
            <label>s inside it would make every radio's accessible name the
            whole group's text. */}
        <div>
          <span id="one-off-purpose-label" className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">Purpose</span>
          <div role="radiogroup" aria-labelledby="one-off-purpose-label" data-testid="send-one-off-purpose" className="flex flex-wrap gap-2">
            {PURPOSES.map((p) => {
              const active = purpose === p.value;
              return (
                <label
                  key={p.value}
                  className={`flex items-start gap-2 rounded-xl border px-3 py-2 cursor-pointer transition-colors ${
                    active ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.06]' : 'border-white/[0.06] bg-white/[0.015] hover:border-white/[0.12]'
                  }`}
                >
                  <input
                    type="radio"
                    name="one-off-purpose"
                    value={p.value}
                    checked={active}
                    onChange={() => setPurpose(p.value)}
                    className="mt-0.5 accent-[#B0FC38]"
                  />
                  <span className="text-[12.5px] text-white">
                    {p.label} <span className="text-gray-500">({p.hint})</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <TemplateEditor
          subject={subject}
          onSubjectChange={setSubject}
          bodyHtml={bodyHtml}
          onBodyHtmlChange={setBodyHtml}
          bodyText={bodyText}
          onBodyTextChange={setBodyText}
          customTokens={customTokens}
          onAddCustomToken={(t) => setCustomTokens((prev) => [...prev, t])}
          sender={users.find((u) => u.id === fromUserId)}
        />

        {error && (
          <div data-testid="send-one-off-error" className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">{error}</div>
        )}
        {notice && (
          <div data-testid="send-one-off-notice" className="px-3 py-2 rounded-lg bg-amber-500/[0.08] border border-amber-500/20 text-[13px] text-amber-300/90">{notice}</div>
        )}
      </div>
    </Modal>
  );
}

const STUB_NOTICE_MS = 1500;

const ERROR_COPY = {
  opted_out: 'This school has opted out of outreach. The email was not sent.',
  suppressed: 'This address is on the suppression list (bounced or unsubscribed). The email was not sent.',
  send_failed: 'The email provider rejected this send. Nothing was delivered.',
};
// Prefer the server's own message; fall back to per-code copy, then generic.
function errorMessage(err) {
  const code = err?.code || err?.data?.code;
  return err?.data?.error || err?.message || ERROR_COPY[code] || 'Failed to send';
}

const selectCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white';
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
